import { Plus } from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { usePager, useAttachments } from '../../api/queries';
import type { AttachmentListFilter, AttachmentState, Network } from '../../api/types';
import { runStatusPresentation } from '../../components/presentation';
import { AttachmentRequestsCell } from '../requests/RequestCountsCell';
import { CopyButton, ErrorMessage, Loading, Pager, StatusBadge } from '../../components/ui';
import { useCardTable } from '../../lib/cardTable';
import { formatDateTime, formatRelative } from '../../lib/format';
import { withParam } from '../../lib/listParams';
import { strings } from '../../strings';
import { WaitingText } from '../components';
import { attachmentPresentation, providerLabel } from '../shared';
import { AttachmentRecovery } from './AttachmentActions';
import { waitsForNetworkApply } from './topology';
import { AttachButton } from './AttachButton';

const p = strings.networks.page;
const STATES: readonly AttachmentState[] = [
  'pending',
  'spoke-ready',
  'connecting',
  'connected',
  'detaching',
  'failed',
];

function oneOf<T extends string>(value: string | null, allowed: readonly T[]): T | null {
  return value !== null && (allowed as readonly string[]).includes(value) ? (value as T) : null;
}

/** "Filter by deployment": the URL's `q`, sent 300 ms after typing stops; `/` focuses it. */
function SearchField({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const [text, setText] = useState(value);
  const [shown, setShown] = useState(value);
  const id = useId();
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  });
  if (shown !== value) {
    setShown(value);
    setText(value);
  }
  useEffect(() => {
    if (text === value) return;
    const timer = setTimeout(() => onChangeRef.current(text), 300);
    return () => clearTimeout(timer);
  }, [text, value]);
  return (
    <div className="search-field">
      <label htmlFor={id} className="sr-only">
        {p.search}
      </label>
      <input
        id={id}
        type="search"
        className="input"
        placeholder={p.search}
        value={text}
        data-list-search
        aria-keyshortcuts="/"
        onChange={(e) => setText(e.target.value)}
      />
      <kbd aria-hidden className="kbd">
        /
      </kbd>
    </div>
  );
}

function FilterSelect({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string | null;
  options: readonly { value: string; label: string }[];
  onChange: (value: string | null) => void;
}) {
  const id = useId();
  return (
    <div className="filter">
      <label htmlFor={id}>{label}</label>
      <select
        id={id}
        className="input"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value || null)}
      >
        <option value="">{strings.common.any}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

/**
 * The Attachments tab and the Overview's List view (spec 11 6.15.7): filters in the URL (`state`, `provider`,
 * `region`, `q`), one request per page, Retry on failed rows, Attach deployments… in the toolbar.
 */
export function AttachmentsTab({
  network,
  onAttach,
  poll,
}: {
  network: Network;
  onAttach: () => void;
  poll: number | false;
}) {
  const [search, setSearch] = useSearchParams();
  const regions = useMemo(
    () =>
      Object.entries(network.settings.providers).flatMap(([provider, s]) =>
        (s?.regions ?? []).map((region) => ({ provider, region })),
      ),
    [network.settings.providers],
  );
  const filter = useMemo<AttachmentListFilter>(
    () => ({
      state: oneOf(search.get('state'), STATES),
      provider: search.get('provider') || null,
      region: search.get('region') || null,
      q: search.get('q') || null,
    }),
    [search],
  );
  const filtered = !!(filter.state || filter.provider || filter.region || filter.q);
  const setParam = (name: string, value: string | null) =>
    setSearch(withParam(search, name, value), { replace: true });
  return (
    <section aria-label={p.tab.attachments}>
      <div role="group" aria-label={p.filters} className="list-toolbar">
        <SearchField value={filter.q ?? ''} onChange={(q) => setParam('q', q.trim() || null)} />
        <FilterSelect
          label={p.state}
          value={filter.state ?? null}
          options={STATES.map((value) => ({ value, label: strings.networks.attachmentState[value] }))}
          onChange={(value) => setParam('state', value)}
        />
        <FilterSelect
          label={p.provider}
          value={filter.provider ?? null}
          options={Object.keys(network.settings.providers).map((value) => ({
            value,
            label: providerLabel(value),
          }))}
          onChange={(value) => setParam('provider', value)}
        />
        <FilterSelect
          label={p.region}
          value={filter.region ?? null}
          options={[...new Set(regions.map((r) => r.region))].map((value) => ({ value, label: value }))}
          onChange={(value) => setParam('region', value)}
        />
        <span className="ml-auto">
          <AttachButton network={network} onAttach={onAttach} />
        </span>
      </div>
      <AttachmentsList
        key={JSON.stringify(filter)}
        network={network}
        filter={filter}
        filtered={filtered}
        onAttach={onAttach}
        poll={poll}
      />
    </section>
  );
}

function AttachmentsList({
  network,
  filter,
  filtered,
  onAttach,
  poll,
}: {
  network: Network;
  filter: AttachmentListFilter;
  filtered: boolean;
  onAttach: () => void;
  poll: number | false;
}) {
  const pager = usePager();
  const page = useAttachments(network.id, filter, pager.cursor, { refetchInterval: poll });
  const cardTable = useCardTable();
  if (page.error) return <ErrorMessage error={page.error} onRetry={() => void page.refetch()} />;
  if (!page.data) return <Loading />;
  const items = page.data.items;
  return (
    <>
      {items.length === 0 ? (
        filtered || pager.hasPrevious ? (
          <p className="help">{p.noMatch}</p>
        ) : (
          <div className="net-empty">
            <p className="help">{p.noAttachments}</p>
            <AttachButton
              network={network}
              onAttach={onAttach}
              icon={<Plus aria-hidden className="size-4" />}
            />
          </div>
        )
      ) : (
        <div className="overflow-x-auto rounded-lg border border-line bg-surface">
          <table ref={cardTable} className="table table-cards" data-testid="attachments-table">
            <thead>
              <tr>
                <th scope="col">{p.columns.deployment}</th>
                <th scope="col">{p.columns.blueprint}</th>
                <th scope="col">{p.columns.providerRegion}</th>
                <th scope="col">{p.columns.cidr}</th>
                <th scope="col">{p.columns.size}</th>
                <th scope="col">{p.columns.state}</th>
                <th scope="col">{p.columns.connectivity}</th>
                <th scope="col">{p.columns.requests}</th>
                <th scope="col">{p.columns.lastConnect}</th>
                <th scope="col">
                  <span className="sr-only">{p.columns.actions}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map((a) => {
                const waits = waitsForNetworkApply(a, network);
                return (
                  <tr key={`${a.deploymentId}/${a.provider}/${a.region}`}>
                    <td>
                      <Link className="link font-medium" to={`/deployments/${a.deploymentId}`}>
                        {a.deploymentName}
                      </Link>
                    </td>
                    <td>
                      <Link className="link" to={`/blueprints/${a.blueprintId}`}>
                        {a.blueprintName}
                      </Link>
                    </td>
                    <td>
                      {providerLabel(a.provider)} / {a.region}
                    </td>
                    <td>
                      <span className="inline-flex items-center gap-1">
                        <span className="font-mono">{a.cidr}</span>
                        <CopyButton value={a.cidr} label={p.copyCidr(a.cidr)} />
                      </span>
                    </td>
                    <td>{strings.networks.sizes[a.size]}</td>
                    <td>
                      <StatusBadge presentation={attachmentPresentation(a.state, waits)} />
                      {a.waitingFor !== null && (
                        <span className="net-wait mt-1 block text-[12px] text-fg-muted">
                          <WaitingText waitingFor={a.waitingFor} />
                        </span>
                      )}
                    </td>
                    <td>{a.state === 'connected' ? a.connectivity : strings.common.dash}</td>
                    <td>
                      <AttachmentRequestsCell
                        attachment={a}
                        enforcing={network.settings.topology.kind === 'micro-segmentation'}
                      />
                    </td>
                    <td>
                      {a.lastConnect ? (
                        <Link
                          className="link"
                          to={`/runs/${a.lastConnect.runId}`}
                          title={formatDateTime(a.lastConnect.at)}
                        >
                          {formatRelative(a.lastConnect.at)} ·{' '}
                          {runStatusPresentation[a.lastConnect.status].label.toLowerCase()}
                        </Link>
                      ) : (
                        strings.common.dash
                      )}
                    </td>
                    <td>
                      <AttachmentRecovery network={network} attachment={a} small />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <Pager
        hasPrevious={pager.hasPrevious}
        nextCursor={page.data.nextCursor}
        onPrevious={pager.previous}
        onNext={pager.next}
      />
    </>
  );
}
